import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function RedcarAndClevelandCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Redcar and Cleveland Pothole Claims | 12,635 Repairs in 2023/24 | Fixtyer"
        description="Redcar and Cleveland Borough Council filled 12,635 potholes in 2023/24 — a 41% spike — while carriageway resurfacing fell to 10.6km and 22% of unclassified roads were RED at the last CVI survey. See what the DfT AMBER rating means for your claim."
        canonical="/councils/redcar-and-cleveland"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Redcar and Cleveland', url: '/councils/redcar-and-cleveland' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'Redcar and Cleveland has a GREEN spend scorecard — can I still claim for pothole damage?',
            answer: 'Yes. The council projects £6.977m capital spend against a £4.434m DfT allocation in 2025/26 and targets 90% preventative maintenance. Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired — not on aggregate spend. The DfT Condition scorecard is AMBER, and the council\'s own data shows unclassified RED failure rising to 22% in 2023 before the switch to AEI.',
          },
          {
            question: 'What if my pothole was on a residential or unclassified road?',
            answer: 'U-roads make up 525.5km — roughly 75% of Redcar and Cleveland\'s 703.3km carriageway network. CVI surveys covered only 25% of U-roads until 2023, when coverage reached 100%. At the last comparable CVI survey, 22% of unclassified roads were in the RED category. From 2024, the council switched to Annual Engineer Inspection (AEI), which the report itself says may indicate a higher proportion of poor condition.',
          },
          {
            question: 'Does the 2024 AEI methodology change weaken my claim?',
            answer: 'Not necessarily — but you need to know which ruler applies. Redcar and Cleveland moved from SCANNER/CVI to AEI in 2024. The council states AEI "may indicate a higher proportion of the network in poor condition compared to SCANNER" because it evaluates entire road sections rather than 10-metre sub-sections. Pre- and post-2024 figures are not directly comparable, and the council says as much in its own report.',
          },
          {
            question: 'Pothole repairs spiked to 12,635 in 2023/24 — does that mean the roads are fixed?',
            answer: 'No. The 12,635 figure is the highest in five years — 41% above the five-year average of roughly 8,940. Carriageway resurfacing fell from 30.61km in 2020/21 to 10.63km in 2024/25. Reactive maintenance still accounted for 20% of spend in 2024/25. High pothole volumes and declining resurfacing can coexist.',
          },
          {
            question: 'Why is Redcar and Cleveland AMBER on best practice despite its asset management platform?',
            answer: 'The DfT Best Practice scorecard is AMBER — not GREEN — even though the council highlights AEI surveys, a dedicated asset management platform, and a five-year forward programme. Best practice ratings turn on published evidence and inspection coverage. The council\'s 2020 U-road CVI data is unavailable due to a cyber-attack, and the methodology change in 2024 creates a break in comparable condition records.',
          },
          {
            question: 'How does Section 58 apply to Redcar and Cleveland\'s inspection regime?',
            answer: 'Section 58 of the Highways Act 1980 lets councils defend claims by proving reasonable maintenance systems. Redcar and Cleveland SCANNER-surveys A, B and C roads at 100% annually, but U-road CVI coverage was only 25% until 2023. Your claim succeeds when evidence shows the specific defect met intervention criteria, was previously reported, or should have been found before you hit it — especially on estate roads where one in five were RED at the last CVI survey.',
          },
        ]}
      />
      <ServiceSchema
        councilName="Redcar and Cleveland Borough Council"
        councilSlug="redcar-and-cleveland"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/redcar-and-cleveland"
        headline="Redcar and Cleveland Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Redcar and Cleveland</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Redcar and Cleveland: 12,635 Potholes Filled as Resurfacing Halved
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Redcar and Cleveland Borough Council spends well above its DfT capital allocation and earns a{' '}
            <strong className="text-emerald-400">GREEN</strong> spend scorecard. Yet the overall rating is{' '}
            <strong className="text-amber-400">AMBER</strong> — because pothole repairs spiked to{' '}
            <strong className="text-rose-400">12,635 in 2023/24</strong> while carriageway resurfacing fell from
            30.6km to 10.6km, and <strong className="text-rose-400">22% of unclassified roads were RED</strong> at
            the last CVI survey on a network where 525.5km of estate roads make up three-quarters of the borough.
          </p>

          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">12,635</div>
            <div className="text-white font-medium mt-2">Potholes filled in 2023/24</div>
            <div className="text-brand-300 text-sm mt-1">
              A 41% spike above the five-year average of roughly 8,940 — while resurfacing dropped to just 10.63km
              and programmed patching replaced thousands of individual pothole fills.
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* The Condition Picture */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What the condition data shows
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of survey data from Redcar and Cleveland's own transparency report — SCANNER and CVI until
            2023, then a switch to Annual Engineer Inspection in 2024
          </p>

          {/* A-roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A-roads (111.1km — 15.8% of network): methodology cliff in 2024</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">1%</td>
                    <td className="px-4 py-2 text-center">14%</td>
                    <td className="px-4 py-2 text-center">85%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">1%</td>
                    <td className="px-4 py-2 text-center">15%</td>
                    <td className="px-4 py-2 text-center">84%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">0%</td>
                    <td className="px-4 py-2 text-center">5%</td>
                    <td className="px-4 py-2 text-center">95%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">0%</td>
                    <td className="px-4 py-2 text-center">5%</td>
                    <td className="px-4 py-2 text-center">95%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024 (AEI)</td>
                    <td className="px-4 py-2 text-center font-bold">1%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">63%</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">36%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              Under SCANNER, A-roads held at 95% GREEN in 2022–23. The 2024 AEI survey reclassified 63% as amber
              and cut green to 36% — not a sudden collapse, but a different measuring standard the council adopted
              proactively in 2024.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">B and C roads (66.7km — 9.5% of network): RED tripled under AEI</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">1%</td>
                    <td className="px-4 py-2 text-center">13%</td>
                    <td className="px-4 py-2 text-center">86%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">1%</td>
                    <td className="px-4 py-2 text-center">17%</td>
                    <td className="px-4 py-2 text-center">82%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">1%</td>
                    <td className="px-4 py-2 text-center">13%</td>
                    <td className="px-4 py-2 text-center">86%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">1%</td>
                    <td className="px-4 py-2 text-center">12%</td>
                    <td className="px-4 py-2 text-center">87%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024 (AEI)</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">6%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">65%</td>
                    <td className="px-4 py-2 text-center font-bold">29%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              SCANNER held B/C roads at 1% RED and 87% GREEN through 2023. AEI in 2024 recorded 6% RED and 65%
              amber — the council notes no A roads are scheduled for resurfacing in 2025/26 despite the new picture.
            </p>
          </div>

          {/* Spend context */}
          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">Above-allocation spend, below-allocation resurfacing</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-emerald-400">£4.434m</div>
                <div className="text-brand-300 text-sm">DfT capital allocation 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">£6.977m</div>
                <div className="text-brand-300 text-sm">Projected capital spend 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">90%</div>
                <div className="text-brand-300 text-sm">Estimated preventative share 2025/26</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              Redcar and Cleveland projects capital spend at roughly{' '}
              <span className="text-emerald-400">157% of its DfT allocation</span> — yet carriageway resurfacing
              fell from 30.61km in 2020/21 to 10.63km in 2024/25. The council is shifting from reactive pothole
              fills towards programmed patching, but the network is still producing defects faster than full
              resurfacing can keep pace.
            </p>
          </div>
        </div>
      </section>

      {/* The U-road blind spot */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The 525.5km estate-road majority
          </h2>
          <p className="text-surface-600 mb-8">
            75% of the borough's carriageway network is unclassified — surveyed at only 25% coverage until 2023
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">U-roads in RED condition</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Survey method</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Network coverage</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100 bg-rose-50">
                  <td className="px-6 py-4 font-mono text-brand-900">2020</td>
                  <td className="px-6 py-4 text-center text-surface-400 italic">Unavailable</td>
                  <td className="px-6 py-4 text-center">CVI</td>
                  <td className="px-6 py-4 text-center">25%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">18%</td>
                  <td className="px-6 py-4 text-center">CVI</td>
                  <td className="px-6 py-4 text-center">25%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">17%</td>
                  <td className="px-6 py-4 text-center">CVI</td>
                  <td className="px-6 py-4 text-center">25%</td>
                </tr>
                <tr className="border-b border-surface-100 bg-rose-50">
                  <td className="px-6 py-4 font-mono text-brand-900">2023</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">22%</td>
                  <td className="px-6 py-4 text-center">CVI</td>
                  <td className="px-6 py-4 text-center">100%</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024</td>
                  <td className="px-6 py-4 text-center font-medium">7%* <span className="text-surface-400 text-xs">(AEI — not comparable)</span></td>
                  <td className="px-6 py-4 text-center">AEI</td>
                  <td className="px-6 py-4 text-center">50%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">The 25% coverage gap</h3>
              <p className="text-surface-600 text-sm mb-3">
                Until 2023, Redcar and Cleveland's Coarse Visual Inspection (CVI) covered only 25% of its
                unclassified network each year. For incidents on estate roads in 2020–22, three-quarters of the
                U-road network had <strong>no annual condition survey at all</strong>.
              </p>
              <p className="text-surface-600 text-sm">
                At the last comparable CVI survey with full coverage (2023), roughly{' '}
                <strong>one in five U-roads was in RED condition</strong> — approximately 115km of residential
                streets across Marske, Guisborough, Eston, Redcar and Saltburn.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">*The 7% asterisk and the 2020 cyber-attack gap</h3>
              <p className="text-amber-800 text-sm mb-3">
                In 2024 the council switched to AEI for unclassified roads. The report states AEI "may indicate a
                higher proportion of the network in poor condition compared to SCANNER" — so a drop from 22% to 7%
                is not evidence of recovery. It is a different ruler.
              </p>
              <p className="text-amber-800 text-sm">
                2020 CVI data is unavailable due to a cyber-attack. That creates another hole in the condition
                record for the year the borough was managing pandemic-era road deterioration.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why this matters for Section 58</h3>
            <p className="text-brand-200 mb-4">
              To rely on the Section 58 defence, a council must show it had a reasonable system for knowing the
              condition of its roads. For Redcar and Cleveland's unclassified network, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Was your road in the 25% of U-roads actually surveyed in the year of your incident?</li>
              <li>• If 22% of U-roads were RED at the 2023 full-coverage survey, what was done about yours?</li>
              <li>• Can pre- and post-2024 condition records be compared after the CVI-to-AEI switch?</li>
              <li>• Does the council's asset management platform have records for your specific road section?</li>
            </ul>
            <p className="text-white font-medium">
              A council cannot claim detailed knowledge of a network it surveyed at 25% coverage — then changed
              how it measures.
            </p>
          </div>
        </div>
      </section>

      {/* The repair conveyor belt */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            44,694 potholes in five years
          </h2>
          <p className="text-surface-600 mb-8">
            The scale of reactive repair tells you how many defects this network produces — and when they spike
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled (estimate)</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Carriageway resurfaced (km)</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Programmed patching (m²)</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">8,143</td>
                  <td className="px-6 py-4 text-center">30.61</td>
                  <td className="px-6 py-4 text-center">0</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">7,417</td>
                  <td className="px-6 py-4 text-center">21.12</td>
                  <td className="px-6 py-4 text-center">0</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">6,964</td>
                  <td className="px-6 py-4 text-center">14.31</td>
                  <td className="px-6 py-4 text-center">0</td>
                </tr>
                <tr className="border-b border-surface-100 bg-rose-50">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">12,635</td>
                  <td className="px-6 py-4 text-center">16.09</td>
                  <td className="px-6 py-4 text-center">601</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center">9,535</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">10.63</td>
                  <td className="px-6 py-4 text-center font-bold text-amber-600">1,845</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">44,694</td>
                  <td className="px-6 py-4 text-center font-bold">93.76</td>
                  <td className="px-6 py-4 text-center font-bold">2,446</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">~24 potholes a day, every day</h3>
              <p className="text-surface-600 text-sm">
                Averaged over five years, Redcar and Cleveland fills around 24 potholes per day. The 2023/24
                spike to 12,635 — roughly 35 per day — coincided with only 16.09km of resurfacing. A network
                producing defects at that rate is one where prior reports and photographic evidence decide claims.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The strategic shift to programmed patching</h3>
              <p className="text-amber-800 text-sm">
                The council's own report notes that from 2023/24, "a greater emphasis is on programmed highway
                patching, rather than reactive individual pothole repairs." Programmed patching rose from 601m²
                to 1,845m² in 2024/25, with 2,000m² planned for 2025/26. Individual pothole counts may fall even
                when underlying surface deterioration continues.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* The AEI methodology admission */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The AEI methodology change
          </h2>
          <p className="text-surface-600 mb-8">
            The council's own explanation for why 2024 condition figures look worse — in its own words
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="While AEI may indicate a higher proportion of the network in poor condition compared to SCANNER, this reflects its whole engineering-led evaluation of entire maintenance lengths. AEI therefore provides a more accurate representation of maintenance needs and aligns with practical delivery on the ground whilst being aligned for the new PAS 2161 survey standards."
              source="Redcar and Cleveland Borough Council Local Highways Maintenance Transparency Report, June 2025"
            />
            <QuoteCard
              quote="In 2024, the Council made the proactive decision to move to Annual Engineer Inspection (AEI) for the classified and unclassified road network. This decision was in line with the DfT widening the scope for local authorities to explore different technologies."
              source="Redcar and Cleveland Borough Council Local Highways Maintenance Transparency Report, June 2025"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">What this admission means</h3>
              <p className="text-surface-600 text-sm mb-3">
                Redcar and Cleveland formally acknowledges that AEI produces worse-looking condition data than
                SCANNER — not because roads suddenly deteriorated, but because the survey evaluates entire road
                sections rather than 10-metre sub-sections.
              </p>
              <p className="text-surface-600 text-sm">
                That is documented knowledge that published GREEN percentages from 2020–23 understate maintenance
                need compared to the 2024 AEI baseline — relevant when a council cites historical condition data
                in a Section 58 defence.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Questions worth asking</h3>
              <ul className="text-amber-800 text-sm space-y-2">
                <li>• Which survey method was used for your road in the year of your incident?</li>
                <li>• Does the council's asset management platform show your section as amber or red under AEI?</li>
                <li>• If AEI recommends resurfacing for your road, why was only a pothole fill done?</li>
                <li>• Can the council compare pre-2024 SCANNER/CVI records with post-2024 AEI in your claim response?</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* What AMBER + GREEN spend means for claims */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming against an above-budget AMBER council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Redcar and Cleveland invests above its DfT allocation — here's how that changes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-surface-50 rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What works in the council's favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend scorecard — projects £6.977m capital against £4.434m DfT allocation
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 90% preventative maintenance targeted for 2025/26
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Dedicated asset management platform with prioritised forward programme
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Proactive AEI adoption ahead of mandatory PAS 2161 transition
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> A, B and C roads surveyed at 100% annually
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a documented Section 58 defence on classified roads. Generic claims will struggle.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What works in yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER condition — 22% of U-roads RED at last CVI survey; AEI shows 63% amber on A-roads
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 75% of network is U-roads — historically surveyed at only 25% coverage
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 12,635 potholes filled in 2023/24 — defects forming faster than resurfacing (10.63km in 2024/25)
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 2020 U-road data lost to cyber-attack — gap in condition records
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Council admits AEI shows worse condition than SCANNER — methodology break in 2024
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The winning strategy here is specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with GREEN spend and a data-led asset management platform, your claim lives or dies on
              the <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, council reports) — proof of actual notice</li>
              <li>• Photos showing the defect's size, depth and visible age (weathered edges, previous patching)</li>
              <li>• The road's class — on a U-road, the 25% CVI coverage gap is your strongest structural argument</li>
              <li>• Timing relative to the 2023/24 pothole spike and the 2024 AEI methodology change</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and cites
              Redcar and Cleveland's own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* Report a pothole CTA */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a pothole in Redcar and Cleveland?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            An above-budget council demands a well-built claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No U-road 25% coverage-gap argument</li>
                <li>• No prior-report search</li>
                <li>• No AEI methodology challenge</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional claim pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ 22% U-road RED rate documented</li>
                <li>✅ 25% CVI coverage gap argued</li>
                <li>✅ 44,694 potholes in five years cited</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Redcar and Cleveland</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=redcar-and-cleveland"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order my Redcar and Cleveland claim pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <a
              href="https://www.redcar-cleveland.gov.uk/roads-and-parking/report-a-pothole"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              Report a pothole to the council
            </a>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently asked questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Redcar and Cleveland has a GREEN spend scorecard — can I still claim for pothole damage?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. The council projects £6.977m capital spend against a £4.434m DfT allocation in 2025/26 and
                targets 90% preventative maintenance. Section 58 turns on whether the specific defect that damaged
                your vehicle was reasonably inspected and repaired — not on aggregate spend. The DfT Condition
                scorecard is AMBER, and the council's own data shows unclassified RED failure rising to 22% in 2023
                before the switch to AEI.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a residential or unclassified road?
              </h3>
              <p className="text-surface-600 text-sm">
                U-roads make up 525.5km — roughly 75% of Redcar and Cleveland's 703.3km carriageway network. CVI
                surveys covered only 25% of U-roads until 2023, when coverage reached 100%. At the last comparable
                CVI survey, 22% of unclassified roads were in the RED category. From 2024, the council switched to
                Annual Engineer Inspection (AEI), which the report itself says may indicate a higher proportion of
                poor condition.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does the 2024 AEI methodology change weaken my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Not necessarily — but you need to know which ruler applies. Redcar and Cleveland moved from
                SCANNER/CVI to AEI in 2024. The council states AEI "may indicate a higher proportion of the network
                in poor condition compared to SCANNER" because it evaluates entire road sections rather than 10-metre
                sub-sections. Pre- and post-2024 figures are not directly comparable, and the council says as much in
                its own report.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Pothole repairs spiked to 12,635 in 2023/24 — does that mean the roads are fixed?
              </h3>
              <p className="text-surface-600 text-sm">
                No. The 12,635 figure is the highest in five years — 41% above the five-year average of roughly
                8,940. Carriageway resurfacing fell from 30.61km in 2020/21 to 10.63km in 2024/25. Reactive
                maintenance still accounted for 20% of spend in 2024/25. High pothole volumes and declining
                resurfacing can coexist.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Why is Redcar and Cleveland AMBER on best practice despite its asset management platform?
              </h3>
              <p className="text-surface-600 text-sm">
                The DfT Best Practice scorecard is AMBER — not GREEN — even though the council highlights AEI
                surveys, a dedicated asset management platform, and a five-year forward programme. Best practice
                ratings turn on published evidence and inspection coverage. The council's 2020 U-road CVI data is
                unavailable due to a cyber-attack, and the methodology change in 2024 creates a break in comparable
                condition records.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                How does Section 58 apply to Redcar and Cleveland's inspection regime?
              </h3>
              <p className="text-surface-600 text-sm">
                Section 58 of the Highways Act 1980 lets councils defend claims by proving reasonable maintenance
                systems. Redcar and Cleveland SCANNER-surveys A, B and C roads at 100% annually, but U-road CVI
                coverage was only 25% until 2023. Your claim succeeds when evidence shows the specific defect met
                intervention criteria, was previously reported, or should have been found before you hit it —
                especially on estate roads where one in five were RED at the last CVI survey.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.redcar-cleveland.gov.uk/roads-and-parking/local-highways-maintenance-transparency-report"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Redcar and Cleveland Borough Council Local Highways Maintenance Transparency Report (June 2025)
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
